import React, { useState, useEffect, useMemo } from "react";
import { 
  Button, 
  Dropdown, 
  Input, 
  Popconfirm, 
  Tooltip, 
  Typography, 
  message, 
  Pagination, 
  Modal, 
  Form, 
  Select,
  Spin,
  Tabs 
} from "antd";
import { 
  FilterOutlined, 
  DownOutlined, 
  GlobalOutlined, 
  LockOutlined, 
  InboxOutlined, 
  TeamOutlined, 
  ClockCircleOutlined, 
  UserOutlined, 
  MoreOutlined 
} from "@ant-design/icons";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft, Plus, Search, Users, Send } from "lucide-react";
import { getAudiences, deleteAudience, updateAudience } from "../../../services/audience.service";
import NoTableData from "../../NoTableData";

const { Text } = Typography;
const { Option } = Select;

const WhatsAppAudienceList = ({ onCreateClick }) => {
  const navigate = useNavigate();
  const [audiences, setAudiences] = useState([]);
  const [searchValue, setSearchValue] = useState("");
  const [debouncedSearchValue, setDebouncedSearchValue] = useState("");
  const [activeCategory, setActiveCategory] = useState("all");
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [sortBy, setSortBy] = useState("recently_modified");
  const [visibilitySelect, setVisibilitySelect] = useState("all");
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });

  const [form] = Form.useForm();
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [editingAudience, setEditingAudience] = useState(null);
  const [isUpdating, setIsUpdating] = useState(false);

  const handleEditClick = (audience) => {
    setEditingAudience(audience);
    form.setFieldsValue({
      name: audience.name,
      visibility: audience.visibility || "PUBLIC"
    });
    setEditModalVisible(true);
  };

  const handleEditSubmit = async (values) => {
    try {
      setIsUpdating(true);
      await updateAudience(editingAudience.id, {
        ...editingAudience,
        ...values
      });
      message.success("Audience updated successfully");
      setEditModalVisible(false);
      fetchAudiences(pagination.current, pagination.pageSize);
    } catch (error) {
      console.error("Failed to update audience", error);
      message.error("Failed to update audience");
    } finally {
      setIsUpdating(false);
    }
  };

  // Debounce the search input value
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearchValue(searchValue);
    }, 500);

    return () => {
      clearTimeout(handler);
    };
  }, [searchValue]);

  const fetchAudiences = async (
    page = 1,
    size = 10,
    currentSortBy = sortBy,
    currentCategory = activeCategory,
    currentVisibilitySelect = visibilitySelect,
    currentSearchValue = debouncedSearchValue
  ) => {
    setLoading(true);
    try {
      let sortParam = "lastModifiedDate,desc";
      if (currentSortBy === "alphabetical") {
        sortParam = "name,asc";
      } else if (currentSortBy === "recently_modified") {
        sortParam = "lastModifiedDate,desc";
      } else if (currentSortBy === "contacts") {
        sortParam = "totalContacts,desc";
      } else if (currentSortBy === "recently_created") {
        sortParam = "createdDate,desc";
      }

      const params = {
        page: page - 1, // Spring backend is 0-indexed
        size,
        sort: sortParam,
      };

      let visibilityParam = null;
      const isPublicRequested = currentCategory === "public" || currentVisibilitySelect === "public";
      const isPrivateRequested = currentCategory === "private" || currentVisibilitySelect === "private";

      if (isPublicRequested && isPrivateRequested) {
        visibilityParam = "CONFLICT";
      } else if (isPublicRequested) {
        visibilityParam = "PUBLIC";
      } else if (isPrivateRequested) {
        visibilityParam = "PRIVATE";
      }

      if (visibilityParam) {
        params["visibility.equals"] = visibilityParam;
      }

      if (currentSearchValue && currentSearchValue.trim()) {
        params["keyword.contains"] = currentSearchValue.trim();
      }
      
      const response = await getAudiences(params);
      if (response && response.data) {
        setAudiences(response.data);
        // Assuming standard JHipster pagination headers
        const totalCount = parseInt(response.headers["x-total-count"], 10) || response.data.length;
        setPagination({
          current: page,
          pageSize: size,
          total: totalCount,
        });
      }
    } catch (error) {
      console.error("Failed to fetch audiences:", error);
      message.error("Failed to load audiences. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAudiences(1, pagination.pageSize, sortBy, activeCategory, visibilitySelect, debouncedSearchValue);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sortBy, activeCategory, visibilitySelect, debouncedSearchValue]);

  const handleTableChange = (newPagination) => {
    fetchAudiences(newPagination.current, newPagination.pageSize);
  };

  const handleDelete = async (id) => {
    try {
      setLoading(true);
      await deleteAudience(id);
      message.success("Audience deleted successfully");
      fetchAudiences(pagination.current, pagination.pageSize);
    } catch (error) {
      console.error("Failed to delete audience:", error);
      message.error("Failed to delete audience. It might be attached to a campaign.");
      setLoading(false);
    }
  };

  const filteredAudiences = useMemo(() => {
    return audiences;
  }, [audiences]);

  const counts = useMemo(() => {
    return {
      all: audiences.length,
      public: audiences.filter(a => a.visibility === "PUBLIC").length,
      private: audiences.filter(a => a.visibility === "PRIVATE").length,
    };
  }, [audiences]);

  // Toggle selection for all
  const handleSelectAll = (e) => {
    if (e.target.checked) {
      setSelectedRowKeys(filteredAudiences.map(a => a.id));
    } else {
      setSelectedRowKeys([]);
    }
  };

  // Toggle selection for single item
  const handleSelectItem = (id, checked) => {
    if (checked) {
      setSelectedRowKeys(prev => [...prev, id]);
    } else {
      setSelectedRowKeys(prev => prev.filter(key => key !== id));
    }
  };

  return (
    <div className="p-20 tw-font-inter tw-bg-white tw-min-h-full tw-overflow-y-auto">
      <div className="tw-max-w-[1400px] tw-mx-auto">
        {/* Header Block matching Campaign Page */}
        <div className="tw-flex tw-flex-col sm:tw-flex-row sm:tw-items-center sm:tw-justify-between tw-gap-4 tw-mb-6">
          <div className="tw-flex tw-items-center tw-gap-3.5">
            <div
              className="tw-flex tw-cursor-pointer tw-items-center tw-justify-center tw-rounded-xl tw-p-2 tw-bg-white tw-border tw-border-solid tw-border-slate-200/80 hover:tw-bg-slate-100/60 tw-text-slate-600 tw-transition-colors tw-shadow-xs tw-flex-shrink-0"
              onClick={() => navigate(-1)}
              role="button"
              aria-label="Back"
            >
              <ArrowLeft size={18} />
            </div>
            <div>
              <h2 className="tw-text-2xl tw-font-bold tw-text-slate-800 tw-font-inter tw-tracking-tight tw-mb-0">
                Lists
              </h2>
              <p className="tw-text-xs tw-text-slate-500 tw-font-medium tw-font-inter tw-mt-1 tw-mb-0">
                Found {pagination.total} List(s)
              </p>
            </div>
          </div>

          {/* Right Side: Actions matching Campaign Page */}
          <div className="tw-flex tw-items-center tw-gap-2.5 tw-flex-wrap">
            <Button
              onClick={() => navigate("/whatsapp-campaigns")}
              className="!tw-h-[38px] !tw-rounded-xl tw-font-semibold tw-border-slate-200 hover:tw-bg-slate-50 tw-text-slate-700 tw-flex tw-items-center tw-gap-2 tw-text-xs md:tw-text-sm"
            >
              <Send className="tw-w-4 tw-h-4 tw-text-slate-500" />
              Campaigns
            </Button>
            <Button
              type="primary"
              onClick={onCreateClick}
              className="!tw-bg-[#5D44FF] hover:!tw-bg-[#4e36e2] !tw-border-[#5D44FF] !tw-text-white !tw-h-[38px] !tw-rounded-xl tw-font-semibold tw-text-xs md:tw-text-sm tw-flex tw-items-center tw-gap-2 tw-shadow-sm tw-shadow-[#5D44FF]/20"
            >
              <Plus size={16} />
              Create Audience
            </Button>
          </div>
        </div>

        {/* Status Filter Tabs with Search & Sort matching Campaign Design */}
        <div id="tabscompany" className="tw-mb-6">
          <Tabs
            activeKey={activeCategory}
            onChange={(key) => {
              setActiveCategory(key);
              setSelectedRowKeys([]);
            }}
            tabBarExtraContent={{
              right: (
                <div className="tw-flex tw-items-center tw-gap-3 tw-pb-1 tw-flex-wrap">
                  <Input
                    value={searchValue}
                    onChange={(e) => setSearchValue(e.target.value)}
                    prefix={<Search className="tw-text-slate-400 tw-w-4 tw-h-4" />}
                    placeholder="Search list..."
                    className="!tw-h-[38px] !tw-rounded-xl tw-border-slate-200 hover:tw-border-slate-300 focus:tw-border-[#5D44FF] tw-w-[220px] sm:tw-w-[280px] tw-font-inter tw-text-xs"
                    allowClear
                  />
                  <Dropdown
                    menu={{
                      items: [
                        { key: "recently_modified", label: "Recently Modified", onClick: () => setSortBy("recently_modified") },
                        { key: "recently_created", label: "Recently Created", onClick: () => setSortBy("recently_created") },
                        { key: "alphabetical", label: "Alphabetical", onClick: () => setSortBy("alphabetical") },
                        { key: "contacts", label: "Contacts Count", onClick: () => setSortBy("contacts") },
                      ],
                    }}
                    trigger={["click"]}
                  >
                    <Button className="!tw-h-[38px] !tw-rounded-xl tw-border-slate-200 hover:tw-bg-slate-50 tw-text-slate-600 tw-text-xs tw-font-semibold tw-flex tw-items-center tw-gap-2">
                      <FilterOutlined className="tw-text-slate-400" />
                      <span>
                        Sort By: {
                          sortBy === "recently_modified" ? "Recently Modified" :
                          sortBy === "recently_created" ? "Recently Created" :
                          sortBy === "alphabetical" ? "Alphabetical" :
                          "Contacts Count"
                        }
                      </span>
                      <DownOutlined className="tw-text-[10px] tw-text-slate-400" />
                    </Button>
                  </Dropdown>
                </div>
              ),
            }}
            className="tw-font-inter !tw-mb-0 [&_.ant-tabs-nav]:!tw-mb-0 [&_.ant-tabs-nav]:!tw-w-full"
            items={[
              {
                key: "all",
                label: (
                  <span className="tw-flex tw-items-center tw-gap-2 tw-text-xs tw-font-semibold tw-py-1">
                    <Users size={15} />
                    <span>All Lists</span>
                    <span
                      className={`tw-inline-flex tw-items-center tw-justify-center tw-px-2 tw-py-0.5 tw-text-[11px] tw-font-bold tw-rounded-full tw-transition-colors ${
                        activeCategory === "all"
                          ? "tw-bg-[#EFEAFF] tw-text-[#5D44FF]"
                          : "tw-bg-slate-100 tw-text-slate-500"
                      }`}
                    >
                      {counts.all}
                    </span>
                  </span>
                ),
              },
              {
                key: "public",
                label: (
                  <span className="tw-flex tw-items-center tw-gap-2 tw-text-xs tw-font-semibold tw-py-1">
                    <GlobalOutlined />
                    <span>Public</span>
                    <span
                      className={`tw-inline-flex tw-items-center tw-justify-center tw-px-2 tw-py-0.5 tw-text-[11px] tw-font-bold tw-rounded-full tw-transition-colors ${
                        activeCategory === "public"
                          ? "tw-bg-[#EFEAFF] tw-text-[#5D44FF]"
                          : "tw-bg-slate-100 tw-text-slate-500"
                      }`}
                    >
                      {counts.public}
                    </span>
                  </span>
                ),
              },
              {
                key: "private",
                label: (
                  <span className="tw-flex tw-items-center tw-gap-2 tw-text-xs tw-font-semibold tw-py-1">
                    <LockOutlined />
                    <span>Private</span>
                    <span
                      className={`tw-inline-flex tw-items-center tw-justify-center tw-px-2 tw-py-0.5 tw-text-[11px] tw-font-bold tw-rounded-full tw-transition-colors ${
                        activeCategory === "private"
                          ? "tw-bg-[#EFEAFF] tw-text-[#5D44FF]"
                          : "tw-bg-slate-100 tw-text-slate-500"
                      }`}
                    >
                      {counts.private}
                    </span>
                  </span>
                ),
              },
            ]}
          />
        </div>

        {/* Content Area - Responsive Grid */}
        <div>
          {loading ? (
            <div className="tw-rounded-2xl tw-border tw-border-solid tw-border-slate-200/80 tw-bg-white tw-flex tw-flex-col tw-items-center tw-justify-center tw-py-20 tw-px-6 tw-shadow-sm">
              <Spin size="large" />
              <div className="tw-mt-4 tw-text-xs tw-font-medium tw-text-slate-500">
                Loading lists...
              </div>
            </div>
          ) : filteredAudiences.length === 0 ? (
            <div className="tw-rounded-2xl tw-border tw-border-solid tw-border-slate-200/80 tw-bg-white tw-flex tw-flex-col tw-items-center tw-justify-center tw-py-20 tw-px-6 tw-shadow-sm tw-text-center">
              <NoTableData text="No Audiences Found" />
              <div className="tw-mt-4">
                <Button 
                  type="primary" 
                  onClick={onCreateClick} 
                  className="!tw-bg-[#5D44FF] hover:!tw-bg-[#4e36e2] !tw-border-[#5D44FF] !tw-text-white !tw-h-[38px] !tw-rounded-xl tw-font-semibold tw-text-xs md:tw-text-sm tw-flex tw-items-center tw-gap-2 tw-shadow-sm tw-shadow-[#5D44FF]/20"
                >
                  <Plus size={16} />
                  Create Audience
                </Button>
              </div>
            </div>
          ) : (
            <>
              <div className="tw-grid tw-grid-cols-1 tw-gap-5">
                {filteredAudiences.map((item) => {
                  const total = item.totalContacts || 0;
                  const waCount = item.whatsappReadyCount || 0;
                  const emailCount = item.emailReadyCount || 0;

                  const waPct = total > 0 ? (waCount / total) * 100 : 0;
                  const emailPct = total > 0 ? (emailCount / total) * 100 : 0;

                  const formattedTotal = total >= 1000 ? `${(total/1000).toFixed(1).replace(".0", "")}k` : total;
                  const formattedWa = waCount >= 1000 ? `${(waCount/1000).toFixed(1).replace(".0", "")}k` : waCount;
                  const formattedEmail = emailCount >= 1000 ? `${(emailCount/1000).toFixed(1).replace(".0", "")}k` : emailCount;

                  const visTag = item.visibility ? item.visibility.charAt(0).toUpperCase() + item.visibility.slice(1).toLowerCase() : "Public";

                  return (
                    <div 
                      key={item.id} 
                      className="tw-bg-white tw-border tw-border-solid tw-rounded-2xl tw-p-5 tw-flex tw-flex-col md:tw-flex-row md:tw-items-center md:tw-justify-between tw-gap-4 tw-transition-all tw-duration-300 tw-border-slate-200/80 tw-shadow-sm hover:tw-border-[#5D44FF]/40 tw-font-inter"
                    >
                      <div className="tw-flex tw-items-start tw-flex-1 tw-min-w-0">
                        <div className="tw-flex-1 tw-min-w-0">
                          <div className="tw-flex tw-items-center tw-gap-3 tw-mb-2 tw-flex-wrap">
                            <Link 
                              to={`/campaigns/audience/${item.id}`} 
                              className="tw-font-bold tw-text-sm md:tw-text-base tw-text-slate-800 hover:tw-text-[#5D44FF] tw-font-inter tw-transition-colors"
                            >
                              {item.name}
                            </Link>
                            <span className={`tw-inline-flex tw-items-center tw-rounded-full tw-px-2.5 tw-py-0.5 tw-text-[11px] tw-font-bold tw-font-inter ${
                              visTag === "Public" 
                                ? "tw-bg-[#E0F2FE] tw-text-[#0284C7]" 
                                : "tw-bg-slate-100 tw-text-slate-600"
                            }`}>
                              {visTag}
                            </span>
                          </div>

                          <div className="tw-flex tw-items-center tw-gap-2 tw-text-xs tw-text-slate-500 tw-font-medium tw-mb-3 tw-font-inter">
                            <TeamOutlined className="tw-text-slate-400" />
                            <span>Contacts : <strong className="tw-text-slate-700 tw-font-bold">{formattedTotal} Contacts</strong></span>
                          </div>

                          <div className="tw-max-w-md tw-w-full">
                            <div className="tw-flex tw-items-center tw-gap-4 tw-mb-1.5 tw-text-[11px] tw-font-bold tw-font-inter">
                              <span className="tw-flex tw-items-center tw-gap-1.5 tw-text-slate-500">
                                <span className="tw-w-2 tw-h-2 tw-rounded-full tw-bg-[#22C55E]"></span>
                                <strong className="tw-text-slate-700">{formattedWa}</strong> WhatsApp
                              </span>
                              <span className="tw-flex tw-items-center tw-gap-1.5 tw-text-slate-500">
                                <span className="tw-w-2 tw-h-2 tw-rounded-full tw-bg-[#5D44FF]"></span>
                                <strong className="tw-text-slate-700">{formattedEmail}</strong> Email
                              </span>
                            </div>
                            
                            <div className="tw-w-full tw-h-2 tw-bg-slate-100 tw-rounded-full tw-overflow-hidden tw-flex">
                              <div className="tw-h-full tw-bg-[#22C55E]" style={{ width: `${waPct}%` }}></div>
                              <div className="tw-h-full tw-bg-[#5D44FF]" style={{ width: `${emailPct}%` }}></div>
                            </div>
                          </div>
                        </div>
                      </div>

                      <div className="tw-flex tw-items-center tw-justify-between md:tw-justify-end tw-gap-6 md:tw-gap-8 tw-flex-shrink-0">
                        <div className="tw-text-xs tw-text-slate-500 tw-flex tw-flex-col tw-gap-1.5 tw-font-medium tw-font-inter">
                          <div className="tw-flex tw-items-center tw-gap-2">
                            <ClockCircleOutlined className="tw-text-slate-400" />
                            <span>Created On : <strong className="tw-text-slate-700 tw-font-semibold">{item.createdDate ? new Date(item.createdDate).toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" }) + " at " + new Date(item.createdDate).toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit" }).toLowerCase() : "N/A"}</strong></span>
                          </div>
                          {(item.createdByName || item.createdBy) && (
                            <div className="tw-flex tw-items-center tw-gap-2">
                              <UserOutlined className="tw-text-slate-400" />
                              <span>Created By : <strong className="tw-text-slate-700 tw-font-semibold">{item.createdByName || item.createdBy}</strong></span>
                            </div>
                          )}
                          {item.lastModifiedDate && (
                            <div className="tw-flex tw-items-center tw-gap-2">
                              <ClockCircleOutlined className="tw-text-slate-400" />
                              <span>Modified On : <strong className="tw-text-slate-700 tw-font-semibold">{new Date(item.lastModifiedDate).toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" }) + " at " + new Date(item.lastModifiedDate).toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit" }).toLowerCase()}</strong></span>
                            </div>
                          )}
                          {(item.lastModifiedByName || item.lastModifiedBy) && (
                            <div className="tw-flex tw-items-center tw-gap-2">
                              <UserOutlined className="tw-text-slate-400" />
                              <span>Modified By : <strong className="tw-text-slate-700 tw-font-semibold">{item.lastModifiedByName || item.lastModifiedBy}</strong></span>
                            </div>
                          )}
                        </div>

                        <Dropdown
                          menu={{
                            items: [
                              {
                                key: "view",
                                label: <Link to={`/campaigns/audience/${item.id}`}>View Details</Link>,
                              },
                              {
                                key: "edit",
                                label: <span onClick={() => handleEditClick(item)}>Edit Audience</span>,
                              },
                              {
                                key: "delete",
                                danger: true,
                                label: (
                                  <Popconfirm
                                    title="Delete Audience"
                                    description="Are you sure to delete this audience?"
                                    onConfirm={() => handleDelete(item.id)}
                                    okText="Yes"
                                    cancelText="No"
                                  >
                                    <span>Delete</span>
                                  </Popconfirm>
                                ),
                              },
                            ],
                          }}
                          trigger={["click"]}
                          placement="bottomRight"
                        >
                          <Button 
                            type="text" 
                            icon={<MoreOutlined className="tw-text-base tw-text-slate-400" />} 
                            className="hover:!tw-bg-slate-100/60 tw-h-9 tw-w-9 tw-flex tw-items-center tw-justify-center !tw-rounded-xl"
                          />
                        </Dropdown>
                      </div>
                    </div>
                  );
                })}
              </div>

              {pagination.total > pagination.pageSize && (
                <div className="tw-mt-6 tw-flex tw-items-center tw-justify-between tw-bg-white tw-px-6 tw-py-3.5 tw-rounded-2xl tw-border tw-border-solid tw-border-slate-200/80 tw-shadow-sm">
                  <span className="tw-text-xs tw-text-slate-500 tw-font-medium">
                    Showing {(pagination.current - 1) * pagination.pageSize + 1}-
                    {Math.min(pagination.current * pagination.pageSize, pagination.total)}{" "}
                    of {pagination.total} lists
                  </span>
                  <Pagination
                    current={pagination.current}
                    pageSize={pagination.pageSize}
                    total={pagination.total}
                    onChange={(page, pageSize) => fetchAudiences(page, pageSize)}
                    showSizeChanger={false}
                  />
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* Edit Audience Modal */}
      <Modal
        title="Edit Audience"
        open={editModalVisible}
        onCancel={() => setEditModalVisible(false)}
        footer={null}
        destroyOnClose
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleEditSubmit}
          className="tw-mt-4"
        >
          <Form.Item
            name="name"
            label="Audience Name"
            rules={[{ required: true, message: "Please enter audience name" }]}
          >
            <Input placeholder="Enter audience name" />
          </Form.Item>
          
          <Form.Item
            name="visibility"
            label="Visibility"
            rules={[{ required: true, message: "Please select visibility" }]}
          >
            <Select placeholder="Select visibility">
              <Option value="PUBLIC">Public</Option>
              <Option value="PRIVATE">Private</Option>
            </Select>
          </Form.Item>

          <div className="tw-flex tw-justify-end tw-gap-2 tw-mt-6">
            <Button onClick={() => setEditModalVisible(false)}>
              Cancel
            </Button>
            <Button type="primary" htmlType="submit" loading={isUpdating}>
              Save Changes
            </Button>
          </div>
        </Form>
      </Modal>
    </div>
  );
};

export default WhatsAppAudienceList;
